import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  SimpleChanges
} from "@angular/core";
import {IScrollProvider, SCROLL_PROVIDER, WindowScrollProvider} from "../scroll";

const REVERT_SCROLL_SPREAD = 10;
const HIDDEN_CLASS = "_hidden";
const SHOWN_CLASS = "_shown";
const STICKY_CLASS = "fixed-sticky-block"; // "sticky-block";

@Directive({
  selector: "[nStickyBlock]"
})

export class NStickyBlockDirective implements AfterViewInit, OnChanges, OnDestroy {

  @Input() nStickyBlock: "top" | "bottom" = "top";
  @Input() stickyTopOffset = 0;
  @Input() stickyBodyInject: boolean;
  @Input() stickyDisabled: boolean;
  @Input() stickOnRevertScroll: boolean;

  @Output() stickChanged = new EventEmitter<boolean>();

  readonly element: HTMLElement;
  private elementParent: HTMLElement;
  private scrollContainer: Element | Window;
  private lastScrollTop: number;
  private scrollProvider: IScrollProvider;
  private placeholder: HTMLDivElement;

  private scrollListener = () => this.onScroll();
  private resizeListener = () => this.onResize();


  constructor(el: ElementRef,
              protected zone: NgZone,
              @Inject(SCROLL_PROVIDER) @Optional() scrollProvider: IScrollProvider) {

    this.scrollProvider = scrollProvider || new WindowScrollProvider();
    this.element = el.nativeElement;
  }

  ngAfterViewInit() {
    if (!this.stickyDisabled) {
      this.link();
    }
  }

  ngOnDestroy() {
    this.unlink();
    this.destroy();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const {stickOnRevertScroll, stickyDisabled} = changes;

    if (stickOnRevertScroll) {
      if (stickOnRevertScroll.currentValue) {
        this.element.classList.add("_on-revert-scroll");
      } else {
        this.element.classList.remove("_on-revert-scroll");
      }
    }

    if (stickyDisabled && !stickyDisabled.firstChange && !!stickyDisabled.currentValue !== !!stickyDisabled.previousValue) {
      if (stickyDisabled.currentValue) {
        this.unlink();
        this.destroy();
      } else {
        this.unlink();
        this.link();
        this.update();
      }
    }
  }

  onResize() {
    this.onScroll();
  }

  onScroll() {
    const rect = this.placeholder ? this.placeholder.getBoundingClientRect() : this.element.getBoundingClientRect();

    if (!rect.width && !rect.height) {
      this.unStick();
      return;
    }

    let stick = false;

    switch (this.nStickyBlock) {
      case "top":
        stick = (this.stickOnRevertScroll ? rect.bottom : rect.top) < this.stickyTopOffset;
        break;
      case "bottom":
        stick = rect.bottom > window.innerHeight;
        break;
    }

    let unhide: boolean;

    if (this.stickOnRevertScroll) {
      const scrollTop = this.scrollContainer instanceof Window ?
        this.scrollContainer.scrollY :
        this.scrollContainer.scrollTop;

      if (typeof(this.lastScrollTop) === "undefined") {
        this.lastScrollTop = scrollTop;
      } else {
        const diff = scrollTop - this.lastScrollTop;

        if (Math.abs(diff) > REVERT_SCROLL_SPREAD) {
          this.lastScrollTop = scrollTop;

          switch (this.nStickyBlock) {
            case "top":
              unhide = diff < 0;
              break;
            case "bottom":
              unhide = diff > 0;
              break;
          }
        }
      }

    }
    if (stick) {
      this.stick(rect.width, rect.height, rect.left, unhide);
    } else {
      this.unStick();
    }
  }

  update() {
    this.onScroll();
  }

  protected link() {
    if (!this.scrollContainer) {
      this.scrollContainer = this.scrollProvider.getScrollContainer();
      this.zone.runOutsideAngular(() => {
        requestAnimationFrame(() => this.onScroll());
        this.scrollContainer.addEventListener("scroll", this.scrollListener);
        window.addEventListener("resize", this.resizeListener);
      });
    }
  }

  protected unlink() {
    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener("scroll", this.scrollListener);
      this.scrollContainer = null;
      window.removeEventListener("resize", this.resizeListener);
    }
  }

  protected  destroy() {
    if (this.elementParent) {
      // если модалка закрылась - вернуть вырванный элемент на место.
      this.elementParent.appendChild(this.element);
      this.clear();
    }
  }

  stick(width: number, height: number, left: number, unhide?: boolean) {
    const isSticky = this.element.classList.contains(STICKY_CLASS);

    if (this.stickOnRevertScroll) {
      if (isSticky) {
        const isHidden = this.element.classList.contains(HIDDEN_CLASS);
        if (typeof(unhide) !== "undefined") {
          // если элемент скрыт, и его нужно отобразить
          if (unhide && isHidden) {
            this.element.classList.remove(HIDDEN_CLASS);
            this.element.classList.add(SHOWN_CLASS);
          }
          // если элемент отображается, и его нужно скрыть
          if (!unhide && !isHidden) {
            this.element.classList.add(HIDDEN_CLASS);
            this.element.classList.remove(SHOWN_CLASS);
          }
        }
      }
    }
    // используется, когда происходит вызов scroll OnResize
    this.element.style.width = width + "px";
    this.element.style.left = left + "px";
    if (isSticky) {
      return;
    }
    this.placeholder = document.createElement("div") as HTMLDivElement;

    this.element.parentNode.insertBefore(this.placeholder, this.nStickyBlock === "top" ? this.element : this.element.nextSibling);

    this.element.classList.add(STICKY_CLASS);
    this.element.classList.add(`_${this.nStickyBlock}`);
    // вырываем текущий элемент в body
    this.placeholder.style.height = height + "px";
    if (this.stickyBodyInject) {
      this.elementParent = this.element.parentElement;
      document.body.appendChild(this.element);
    }

    this.zone.runGuarded(() => {
      this.stickChanged.emit(true);
    });
  }

  unStick() {
    if (!this.element.classList.contains(STICKY_CLASS)) {
      return;
    }

    this.element.style.width = null;
    this.element.style.left = null;
    if (this.stickyBodyInject) {
      this.elementParent.appendChild(this.element);
    }
    this.clear();
    this.zone.runGuarded(() => {
      this.stickChanged.emit(false);
    });
  }

  private clear() {
    if (this.placeholder) {
      this.placeholder.style.height = null;
      this.placeholder.parentElement.removeChild(this.placeholder);
      this.placeholder = null;
    }
    this.element.classList.remove(STICKY_CLASS);
    this.element.classList.remove(SHOWN_CLASS);
    this.element.classList.remove(HIDDEN_CLASS);
    this.element.classList.remove(`_${this.nStickyBlock}`);
  }
}
